import { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import toast from 'react-hot-toast';
import * as XLSX from 'xlsx';
import { Header } from '../components/Header';
import { PageHeader } from '../components/PageHeader';
import { PageLayout } from '../components/PageLayout';
import { interactiveCampaignApi } from '../services/interactiveCampaignApi';

const cleanPhoneNumber = (phone: string): string => {
  if (!phone) return phone;
  return phone.replace(/@c\.us$/, '').replace(/@lid$/, '').trim();
};

function getStatusBadge(status: string) {
  const map: Record<string, { label: string; className: string }> = {
    DRAFT: { label: 'Rascunho', className: 'bg-gray-100 text-gray-800 dark:bg-gray-800/50 dark:text-gray-300' },
    SCHEDULED: { label: 'Agendada', className: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300' },
    STARTED: { label: 'Iniciada', className: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300' },
    PAUSED: { label: 'Pausada', className: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300' },
    COMPLETED: { label: 'Concluída', className: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300' },
  };
  return map[status] || { label: status, className: 'bg-gray-100 text-gray-800 dark:bg-gray-800/50' };
}

export function InteractiveCampaignReportPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const isBot = location.pathname.includes('/campanhas/bot/');

  const [reportData, setReportData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'dashboard' | 'details'>('dashboard');

  const listPath = isBot ? '/campanhas/bot' : '/campanhas/interativa';
  const listLabel = isBot ? 'Chatbot / Fluxos' : 'Campanha Interativa';
  const reportTitle = isBot ? 'Relatório do Bot' : 'Relatório da Campanha Interativa';

  const loadReport = async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const data = await interactiveCampaignApi.getCampaignReport(id);
      setReportData(data);
    } catch (e: any) {
      setError(e?.message || 'Erro ao carregar relatório');
      setReportData(null);
      toast.error(e?.message || 'Erro ao carregar relatório');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadReport();
  }, [id]);

  const handleExportToExcel = () => {
    if (!reportData) return;
    try {
      const exportData = reportData.campaign.sessions.map((session: any) => {
        const row: any = {
          Nome: session.contactName,
          Telefone: cleanPhoneNumber(session.contactPhone),
          Status: session.status === 'ACTIVE' ? 'Ativa' : session.status === 'COMPLETED' ? 'Concluída' : session.status === 'FAILED' ? 'Falhada' : 'Expirada',
        };
        if (reportData.flowNodes) {
          reportData.flowNodes.forEach((node: any) => {
            const nodeData = session.visitedNodes?.[node.id];
            row[`${node.label} (${node.type})`] = nodeData
              ? (nodeData.sent ? `✓ ${new Date(nodeData.visitedAt).toLocaleString('pt-BR')}` : `✗ ${nodeData.error || 'Falhou'}`)
              : '-';
          });
        }
        return row;
      });
      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(exportData);
      const colWidths = [{ wch: 20 }, { wch: 15 }, { wch: 12 }, ...(reportData.flowNodes || []).map(() => ({ wch: 25 }))];
      ws['!cols'] = colWidths;
      XLSX.utils.book_append_sheet(wb, ws, 'Relatório');
      XLSX.writeFile(wb, `relatorio_${reportData.campaign.name}_${new Date().toISOString().split('T')[0]}.xlsx`);
      toast.success('Relatório exportado com sucesso!');
    } catch (err: any) {
      toast.error(err?.message || 'Erro ao exportar');
    }
  };

  if (!id) {
    return (
      <>
        <Header title={reportTitle} compactTopbar />
        <PageLayout>
          <div className="card-padrao p-8 text-center">
            <p className="text-gray-600 dark:text-gray-400">ID não informado.</p>
            <button type="button" onClick={() => navigate(listPath)} className="mt-4 btn-atualizar">
              <i className="fas fa-arrow-left" aria-hidden /> Voltar
            </button>
          </div>
        </PageLayout>
      </>
    );
  }

  if (error && !reportData) {
    return (
      <>
        <Header title={reportTitle} compactTopbar />
        <PageLayout>
          <PageHeader
            title={reportTitle}
            breadcrumbs={[
              { label: 'Campanhas', href: '/campanhas' },
              { label: listLabel, href: listPath },
              { label: 'Relatório' },
            ]}
          />
          <div className="card-padrao p-8 text-center">
            <p className="text-red-600 dark:text-red-400">{error}</p>
            <button type="button" onClick={() => navigate(listPath)} className="mt-4 btn-atualizar">
              <i className="fas fa-arrow-left" aria-hidden /> Voltar
            </button>
          </div>
        </PageLayout>
      </>
    );
  }

  const campaign = reportData?.campaign;
  const stats = reportData?.stats;

  return (
    <>
      <Header
        title={campaign?.name ? `${reportTitle}: ${campaign.name}` : reportTitle}
        compactTopbar
      />
      <PageLayout>
        <PageHeader
          title={campaign?.name ? `Relatório: ${campaign.name}` : reportTitle}
          breadcrumbs={[
            { label: 'Campanhas', href: '/campanhas' },
            { label: listLabel, href: listPath },
            { label: campaign?.name || 'Relatório' },
          ]}
        />

            {loading ? (
          <div className="card-padrao flex items-center justify-center py-16">
            <div className="animate-spin rounded-full h-8 w-8 border-2 border-gray-200 dark:border-gray-600 border-t-emerald-500" />
            <span className="ml-3 text-sm text-gray-500 dark:text-gray-400">Carregando relatório...</span>
          </div>
        ) : reportData ? (
          <div className="space-y-6">
            {/* Ações: Atualizar, Voltar, Exportar */}
            <div className="card-padrao p-6">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <h3 className="font-semibold text-gray-800 dark:text-white/90">Relatório</h3>
                <div className="flex items-center gap-2 flex-wrap justify-end">
                  <button type="button" onClick={loadReport} disabled={loading} className="btn-atualizar">
                    <i className={`fas fa-arrows-rotate text-base shrink-0 ${loading ? 'animate-spin' : ''}`} aria-hidden />
                    {loading ? 'Atualizando...' : 'Atualizar'}
                  </button>
                  <button
                    type="button"
                    onClick={handleExportToExcel}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium text-white bg-emerald-600 hover:bg-emerald-700 focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2 dark:focus:ring-offset-gray-900"
                  >
                    <i className="fas fa-file-excel text-base shrink-0" aria-hidden />
                    Exportar Excel
                      </button>
                </div>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm mt-4">
                <div>
                  <span className="font-medium text-gray-600 dark:text-gray-400">Status:</span>
                  <span className={`ml-2 inline-flex items-center px-2.5 py-0.5 rounded-lg text-xs font-medium ${getStatusBadge(campaign?.status).className}`}>
                    {getStatusBadge(campaign?.status).label}
                  </span>
                </div>
                <div>
                  <span className="font-medium text-gray-600 dark:text-gray-400">Conexão:</span>
                  <span className="ml-2 text-gray-900 dark:text-white/90">
                  {campaign?.connection?.displayName || campaign?.connection?.mePushName || campaign?.connection?.instanceName || 'N/A'}
                </span>
                </div>
                <div>
                  <span className="font-medium text-gray-600 dark:text-gray-400">Criada em:</span>
                  <span className="ml-2 text-gray-900 dark:text-white/90">{campaign?.createdAt ? new Date(campaign.createdAt).toLocaleString('pt-BR') : 'N/A'}</span>
                </div>
              </div>
            </div>

            {/* Tabs */}
            <div className="border-b border-gray-200 dark:border-gray-700">
              <nav className="flex -mb-px gap-4">
                <button
                  type="button"
                  onClick={() => setActiveTab('dashboard')}
                  className={`py-3 px-1 text-sm font-medium border-b-2 transition-colors ${
                    activeTab === 'dashboard'
                      ? 'border-emerald-500 text-emerald-600 dark:text-emerald-400'
                      : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
                  }`}
                >
                  Dashboard
                </button>
                <button
                  type="button"
                  onClick={() => setActiveTab('details')}
                  className={`py-3 px-1 text-sm font-medium border-b-2 transition-colors ${
                    activeTab === 'details'
                      ? 'border-emerald-500 text-emerald-600 dark:text-emerald-400'
                      : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
                  }`}
                >
                  Detalhes
                </button>
              </nav>
            </div>

            {activeTab === 'dashboard' && (
              <div className="space-y-6">
                {/* KPIs */}
                <div className="card-padrao p-6">
                  <h3 className="font-semibold text-gray-800 dark:text-white/90 mb-1">Estatísticas de sessões</h3>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">Contatos que interagiram com este bot (sessões = conversas por contato). Ativas: em andamento. Concluídas: chegaram ao fim do fluxo. Falhadas: erro no envio.</p>
                  <div className="grid grid-cols-1 rounded-xl border border-gray-200 sm:grid-cols-2 lg:grid-cols-4 lg:divide-x lg:divide-y-0 dark:divide-gray-800 dark:border-gray-800">
                    <div className="border-b p-5 sm:border-r lg:border-b-0">
                      <div className="mb-1.5 flex items-center gap-2">
                        <span className="text-blue-600 dark:text-blue-400 text-xl"><i className="fas fa-address-book" aria-hidden /></span>
                        <p className="text-sm text-gray-400 dark:text-gray-500">Total de contatos</p>
                      </div>
                      <h3 className="text-3xl text-gray-800 dark:text-white/90">{stats?.total ?? 0}</h3>
                    </div>
                    <div className="border-b p-5 lg:border-b-0">
                      <div className="mb-1.5 flex items-center gap-2">
                        <span className="text-emerald-600 dark:text-emerald-400 text-xl"><i className="fas fa-circle-play" aria-hidden /></span>
                        <p className="text-sm text-gray-400 dark:text-gray-500">Sessões ativas</p>
                      </div>
                      <h3 className="text-3xl text-gray-800 dark:text-white/90">{stats?.active ?? 0}</h3>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        {stats?.total > 0 ? `${((stats.active / stats.total) * 100).toFixed(1)}%` : '0%'} do total
                      </p>
                    </div>
                    <div className="border-b p-5 sm:border-r sm:border-b-0">
                      <div className="mb-1.5 flex items-center gap-2">
                        <span className="text-purple-600 dark:text-purple-400 text-xl"><i className="fas fa-circle-check" aria-hidden /></span>
                        <p className="text-sm text-gray-400 dark:text-gray-500">Concluídas</p>
                      </div>
                      <h3 className="text-3xl text-gray-800 dark:text-white/90">{stats?.completed ?? 0}</h3>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        {stats?.total > 0 ? `${((stats.completed / stats.total) * 100).toFixed(1)}%` : '0%'} conclusão
                      </p>
                    </div>
                    <div className="p-5">
                      <div className="mb-1.5 flex items-center gap-2">
                        <span className="text-red-600 dark:text-red-400 text-xl"><i className="fas fa-times-circle" aria-hidden /></span>
                        <p className="text-sm text-gray-400 dark:text-gray-500">Falhadas</p>
                      </div>
                      <h3 className="text-3xl text-gray-800 dark:text-white/90">{stats?.failed ?? 0}</h3>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        {stats?.total > 0 ? `${((stats.failed / stats.total) * 100).toFixed(1)}%` : '0%'} erro
                      </p>
                    </div>
                  </div>
                  {(stats?.expired ?? 0) > 0 && (
                    <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                      <div className="flex items-center gap-2">
                        <span className="text-amber-600 dark:text-amber-400 text-xl"><i className="fas fa-clock" aria-hidden /></span>
                        <p className="text-sm text-gray-400 dark:text-gray-500">Expiradas:</p>
                        <span className="text-lg font-semibold text-gray-800 dark:text-white/90">{stats.expired}</span>
                      </div>
                    </div>
                  )}
                </div>

                {/* Funil por etapa */}
                {reportData.flowNodes && reportData.flowNodes.length > 0 && (
                  <div className="card-padrao p-6">
                    <h3 className="font-semibold text-gray-800 dark:text-white/90 mb-1">Funil de conversão por etapa</h3>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">Contatos que receberam cada mensagem do fluxo. Os números são preenchidos quando o bot envia a etapa (após conclusões recentes).</p>
                    <div className="space-y-3">
                      {reportData.flowNodes.map((node: any, index: number) => {
                        const reachedCount = reportData.campaign.sessions.filter((s: any) => s.visitedNodes?.[node.id]).length;
                        const sentCount = reportData.campaign.sessions.filter((s: any) => s.visitedNodes?.[node.id]?.sent).length;
                        const failCount = reachedCount - sentCount;
                        const percentage = stats?.total > 0 ? (sentCount / stats.total) * 100 : 0;
                        return (
                          <div key={node.id} className="relative">
                            <div className="flex items-center gap-4">
                              <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-emerald-500/20 dark:bg-emerald-500/10 flex items-center justify-center text-emerald-700 dark:text-emerald-400 font-bold">
                                {index + 1}
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex flex-wrap items-center justify-between gap-2 mb-1">
                                  <div className="flex items-center gap-2 flex-wrap">
                                    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                                      node.type === 'text' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300' :
                                      node.type === 'image' ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300' :
                                      node.type === 'video' ? 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300' :
                                      'bg-gray-100 text-gray-800 dark:bg-gray-800/50 dark:text-gray-300'
                                    }`}>
                                      {node.type?.toUpperCase() || 'NÓ'}
                                    </span>
                                    <span className="font-medium text-gray-700 dark:text-gray-300 truncate">{node.label}</span>
                                  </div>
                                  <div className="flex items-center gap-3 text-sm text-gray-600 dark:text-gray-400">
                                    <span><strong className="text-green-600 dark:text-green-400">{sentCount}</strong> enviados</span>
                                    {failCount > 0 && <span className="text-red-600 dark:text-red-400">({failCount} falhas)</span>}
                                    <span className="font-semibold text-emerald-600 dark:text-emerald-400">{percentage.toFixed(1)}%</span>
                                  </div>
                                </div>
                                <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2.5 overflow-hidden">
                                  <div
                                    className="h-full bg-emerald-500 dark:bg-emerald-600 rounded-full transition-all"
                                    style={{ width: `${percentage}%` }}
                                  />
                                </div>
                              </div>
                            </div>
                            {index < reportData.flowNodes.length - 1 && (
                              <div className="ml-5 my-2 w-0.5 h-4 bg-gray-200 dark:bg-gray-700" />
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Taxa de conversão e insights */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="card-padrao p-6">
                    <h3 className="font-semibold text-gray-800 dark:text-white/90 mb-1">Taxa de conversão</h3>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">Baseado no status das sessões (concluídas = chegaram ao fim do fluxo; ativas = em andamento).</p>
                    <div className="space-y-4">
                      <div>
                        <div className="flex justify-between items-center mb-2">
                          <span className="text-sm text-gray-600 dark:text-gray-400">Conclusão da jornada</span>
                          <span className="font-semibold text-purple-600 dark:text-purple-400">
                            {stats?.total > 0 ? ((stats.completed / stats.total) * 100).toFixed(1) : '0'}%
                          </span>
                        </div>
                        <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2.5">
                          <div className="h-full bg-purple-500 dark:bg-purple-600 rounded-full" style={{ width: `${stats?.total > 0 ? (stats.completed / stats.total) * 100 : 0}%` }} />
                        </div>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Sessões com status Concluída (fim do fluxo).</p>
                      </div>
                      <div>
                        <div className="flex justify-between items-center mb-2">
                          <span className="text-sm text-gray-600 dark:text-gray-400">Sessões em andamento</span>
                          <span className="font-semibold text-emerald-600 dark:text-emerald-400">
                            {stats?.total > 0 ? ((stats.active / stats.total) * 100).toFixed(1) : '0'}%
                          </span>
                        </div>
                        <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2.5">
                          <div className="h-full bg-emerald-500 dark:bg-emerald-600 rounded-full" style={{ width: `${stats?.total > 0 ? (stats.active / stats.total) * 100 : 0}%` }} />
                        </div>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Sessões ainda ativas (não concluídas nem expiradas).</p>
                      </div>
                      <div>
                        <div className="flex justify-between items-center mb-2">
                          <span className="text-sm text-gray-600 dark:text-gray-400">Taxa de erro</span>
                          <span className="font-semibold text-red-600 dark:text-red-400">
                            {stats?.total > 0 ? ((stats.failed / stats.total) * 100).toFixed(1) : '0'}%
                          </span>
                        </div>
                        <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2.5">
                          <div className="h-full bg-red-500 dark:bg-red-600 rounded-full" style={{ width: `${stats?.total > 0 ? (stats.failed / stats.total) * 100 : 0}%` }} />
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="card-padrao p-6">
                    <h3 className="font-semibold text-gray-800 dark:text-white/90 mb-4">Insights</h3>
                    <div className="space-y-3">
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        Insights detalhados do funil serão exibidos aqui nas próximas versões.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'details' && (
              <div className="card-padrao overflow-hidden">
                <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                  <h3 className="font-semibold text-gray-800 dark:text-white/90">Detalhes das sessões</h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                    Cada linha é uma <strong>conversa</strong> (sessão) que passou pelo bot. <strong>Nome</strong> e <strong>Telefone</strong> vêm do contato (quando “Salvar na lista” está ativo). <strong>Status</strong>: Ativa = em andamento, Concluída = finalizou o fluxo, Expirada = sem resposta no tempo. As colunas à direita mostram em quais <strong>etapas do fluxo</strong> a mensagem foi enviada (✓) ou falhou (✗). <strong>Motivo</strong> só aparece quando a sessão falhou.
                  </p>
                </div>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                    <thead className="bg-gray-50 dark:bg-gray-800/50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Nome</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Telefone</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Status</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider" title="Motivo do erro quando a sessão falha">Motivo (se falhou)</th>
                        {reportData.flowNodes?.map((node: any) => (
                          <th key={node.id} className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider min-w-[120px]">
                            <span className="truncate block max-w-[100px]" title={node.label}>{node.label}</span>
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="bg-white dark:bg-white/[0.02] divide-y divide-gray-200 dark:divide-gray-700">
                      {reportData.campaign.sessions.map((session: any) => (
                        <tr key={session.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/30">
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white/90">{session.contactName || '–'}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-400">{cleanPhoneNumber(session.contactTelefone || session.contactPhone)}</td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-lg text-xs font-medium ${
                              session.status === 'ACTIVE' ? 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300' :
                              session.status === 'COMPLETED' ? 'bg-purple-50 dark:bg-purple-900/20 text-purple-700 dark:text-purple-300' :
                              session.status === 'FAILED' ? 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300' :
                              'bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-300'
                            }`}>
                              {session.status === 'ACTIVE' ? 'Ativa' : session.status === 'COMPLETED' ? 'Concluída' : session.status === 'FAILED' ? 'Falhada' : 'Expirada'}
                            </span>
                          </td>
                          <td className="px-6 py-4 text-sm max-w-xs">
                            {session.status === 'FAILED' && session.variables?.errorReason ? (
                              <span className="text-red-600 dark:text-red-400 text-xs break-words">{session.variables.errorReason}</span>
                            ) : (
                              <span className="text-gray-400 dark:text-gray-500">–</span>
                            )}
                          </td>
                          {reportData.flowNodes?.map((node: any) => {
                            const nodeData = session.visitedNodes?.[node.id];
                            return (
                              <td key={node.id} className="px-6 py-4 text-center">
                                {nodeData ? (
                                  <div className="flex flex-col items-center gap-0.5">
                                    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${nodeData.sent ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300' : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'}`}>
                                      {nodeData.sent ? '✓ Enviado' : '✗ Falhou'}
                                    </span>
                                    {nodeData.visitedAt && <span className="text-xs text-gray-500 dark:text-gray-400">{new Date(nodeData.visitedAt).toLocaleTimeString('pt-BR')}</span>}
                                    {nodeData.error && <span className="text-xs text-red-600 dark:text-red-400 truncate max-w-[100px]" title={nodeData.error}>{nodeData.error}</span>}
                                  </div>
                                ) : (
                                  <span className="text-gray-400 dark:text-gray-500 text-xs">–</span>
                                )}
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {(!reportData.campaign.sessions || reportData.campaign.sessions.length === 0) && (
                  <div className="text-center py-12 text-gray-500 dark:text-gray-400">Nenhuma sessão encontrada</div>
                )}
              </div>
            )}
          </div>
        ) : null}
      </PageLayout>
    </>
  );
}
